// Local navigation and viewing only. No analytics, storage, cookies or answer transmission.
export function promotionActive(start, end, now = Date.now()) {
  const iso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[+-]\d{2}:\d{2})$/;
  if (!iso.test(start || '') || !iso.test(end || '')) return false;
  const a = Date.parse(start), b = Date.parse(end);
  return Number.isFinite(a) && Number.isFinite(b) && b > a && now >= a && now < b;
}

class PdcSamples extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.links = [...this.querySelectorAll('[data-pdc-sample]')];
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return; // ordinary image links remain usable
    this.links.forEach((link, index) => link.addEventListener('click', event => {
      // Preserve modified clicks and the downloadable/open-in-new-tab fallback.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.opener = link;
      this.show(index);
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      this.querySelector('[data-sample-close]').focus();
    }, { signal }));
    this.querySelector('[data-sample-close]').addEventListener('click', () => this.dialog.close(), { signal });
    this.querySelector('[data-sample-prev]').addEventListener('click', () => this.show(this.index - 1), { signal });
    this.querySelector('[data-sample-next]').addEventListener('click', () => this.show(this.index + 1), { signal });
    this.querySelector('[data-sample-zoom]').addEventListener('click', () => {
      const zoom = this.dialog.toggleAttribute('data-zoomed');
      const button = this.querySelector('[data-sample-zoom]');
      button.setAttribute('aria-pressed', String(zoom));
      button.textContent = zoom ? 'Ajustar página' : 'Ampliar texto';
    }, { signal });
    this.dialog.addEventListener('keydown', event => {
      if (event.target.matches?.('input, textarea, select')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }, { signal });
    // The native dialog handles Escape and keyboard focus containment.
    this.dialog.addEventListener('close', () => this.restore(), { signal });
  }
  show(index) {
    if (index < 0 || index >= this.links.length) return;
    this.index = index;
    const link = this.links[index], image = this.querySelector('[data-sample-full]');
    image.src = link.href;
    image.alt = link.dataset.title;
    this.querySelector('[data-sample-title]').textContent = link.dataset.title;
    this.querySelector('[data-sample-position]').textContent = `Muestra ${index + 1} de ${this.links.length}. Usá «Ampliar texto» y desplazate por la página para leerla.`;
    this.querySelector('[data-sample-prev]').disabled = index === 0;
    this.querySelector('[data-sample-next]').disabled = index === this.links.length - 1;
    this.dialog.removeAttribute('data-zoomed');
    const zoom = this.querySelector('[data-sample-zoom]');
    zoom.setAttribute('aria-pressed', 'false');
    zoom.textContent = 'Ampliar texto';
    const scroll = this.querySelector('.pdc-sample-scroll');
    scroll.scrollTop = 0; scroll.scrollLeft = 0;
  }
  restore() {
    if (this.previousOverflow !== undefined) {
      document.documentElement.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
    if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
  }
  disconnectedCallback() {
    if (this.dialog?.open) this.dialog.close();
    this.restore();
    this.abort?.abort();
  }
}
class PdcSituations extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.querySelectorAll('details').forEach(details => details.addEventListener('toggle', () => {
      if (!details.open) return;
      this.querySelectorAll('details').forEach(other => { if (other !== details) other.open = false; });
    }, { signal: this.abort.signal }));
  }
  disconnectedCallback() { this.abort?.abort(); }
}
class PdcPromotion extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.former = this.querySelector('[data-pdc-former]');
    this.initialFormer = this.querySelector('[data-pdc-former-value]')?.textContent.trim();
    this.closest('pdc-purchase')?.addEventListener('change', () => this.update(), { signal: this.abort.signal });
    document.addEventListener('visibilitychange', () => this.update(), { signal: this.abort.signal });
    window.addEventListener('pageshow', () => this.update(), { signal: this.abort.signal });
    this.update();
  }
  update() {
    clearTimeout(this.timer);
    const now = Date.now();
    this.hidden = !promotionActive(this.dataset.start, this.dataset.end, now);
    const select = this.closest('pdc-purchase')?.querySelector('select');
    const former = select ? select.selectedOptions[0]?.dataset.former : this.initialFormer;
    if (this.former) {
      this.former.hidden = this.hidden || !former;
      this.querySelector('[data-pdc-former-value]').textContent = former || '';
    }
    const boundary = now < Date.parse(this.dataset.start) ? Date.parse(this.dataset.start) : Date.parse(this.dataset.end);
    if (Number.isFinite(boundary) && boundary > now) this.timer = setTimeout(() => this.update(), Math.min(boundary - now + 20, 60000));
  }
  disconnectedCallback() { clearTimeout(this.timer); this.abort?.abort(); }
}
class PdcSticky extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.refresh = () => {
      this.hero = document.querySelector('.pdc-hero:not(.pdc-recap) pdc-purchase');
      this.heroButton = this.hero?.querySelector('button[type="submit"]');
      this.schedule();
    };
    this.schedule = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    };
    window.addEventListener('scroll', this.schedule, { passive: true, signal });
    window.addEventListener('resize', this.schedule, { passive: true, signal });
    document.addEventListener('focusin', this.schedule, { signal });
    document.addEventListener('focusout', this.schedule, { signal });
    document.addEventListener('change', this.schedule, { signal });
    document.addEventListener('shopify:section:load', this.refresh, { signal });
    document.addEventListener('shopify:section:unload', () => queueMicrotask(this.refresh), { signal });
    document.addEventListener('shopify:section:reorder', this.refresh, { signal });
    this.observer = new MutationObserver(this.schedule);
    this.observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'data-pdc-cart-busy'] });
    this.observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-pdc-cart-busy'] });
    this.refresh();
  }
  update() {
    const button = this.heroButton;
    const select = this.hero?.querySelector('select');
    const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
    const footer = document.querySelector('footer');
    const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
    const overlay = document.querySelector('dialog[open], #cart-drawer[open]');
    const belowHeroAction = button?.getBoundingClientRect().bottom < 0;
    const show = window.innerWidth <= 767 && button && belowHeroAction && !button.disabled && !editing && !footerVisible && !overlay && (!select || select.value);
    this.hidden = !show;
    if (show) this.querySelector('[data-sticky-price]').textContent = this.hero.querySelector('[data-price]').textContent.trim();
  }
  disconnectedCallback() {
    this.abort?.abort();this.observer?.disconnect();cancelAnimationFrame(this.frame);
  }
}
for (const [name, component] of [['pdc-samples', PdcSamples], ['pdc-situations', PdcSituations], ['pdc-promotion', PdcPromotion], ['pdc-sticky', PdcSticky]]) {
  if (!customElements.get(name)) customElements.define(name, component);
}

function revealContact() {
  if (window.location.hash !== '#pdc-ayuda') return;
  const details = document.getElementById('pdc-ayuda');
  if (details) details.open = true;
}
window.addEventListener('hashchange', revealContact);
document.addEventListener('click', event => {
  const link = event.target instanceof Element ? event.target.closest('a[href="#pdc-ayuda"]') : null;
  if (link) { const details = document.getElementById('pdc-ayuda'); if (details) details.open = true; }
});
revealContact();
